import { ExportFormat, FilterAccess } from '@ecosystem/newron-client-lib';
import { Dispatch, SetStateAction } from 'react';
import { ExportTrackingPageView } from '@hooks/export/export-tracking/ExportTracking';
import { PerformanceAnalysisView } from '@components/performance-analysis-card/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import { PagesConfig } from './PagesConfigContext';
import { LoadedCountries, LoadedProductGroups } from '@hooks/export/Model';

export enum PageType {
  CATEGORY_LEADERBOARD,
  RETAILER_OVERVIEW,
  OPPORTUNITY_REVIEW,
  //PERFORMANCE_ANALYSIS not in use, do not populate in this page
  PERFORMANCE_ANALYSIS,
  RI_PRODUCT,
  TRADING_ASSISTANT,
  EXPLORE,
}

export enum PageName {
  CATEGORY_LEADERBOARD = 'Category Leaderboard',
  RETAILER_OVERVIEW = 'Retailer Overview',
  OPPORTUNITY_REVIEW = 'Opportunity Review',
  PERFORMANCE_ANALYSIS = 'Performance Analysis',
  TRADING_ASSISTANT = 'Trading Assistant',
  RI_PRODUCT = 'Products',
  RI_PRODUCT_COMPARE = 'Products Compare Model',
  RI_PRODUCT_TRENDS = 'Products Trends',
  EXPLORE = 'Explore',
}

export enum SubSection {
  EXPLORE_LEADERBOARD = 'Leaderboard(Explore)',
  EXPLORE_TRENDS = 'Trends(Explore)',
  EXPLORE_SEGMENTS = 'Segments(Explore)',
  TRADING_ASSISTANT_SUPPLIER_REVIEW = 'Supplier Review(Trading Assistant)',
  PRODUCT_OVERVIEW = 'Overview(Product)',
  PRODUCT_TRENDS = 'Trends(Product)',
  PRODUCT_LEADERBOARD = 'Leaderboard(Product)',
}

export type PageTypeAndName = {
  pageType: PageType;
  pageName?: PageName;
};

export enum FilterKeys {
  PRODUCT_GROUP,
  COUNTRY,
  ONLINE_OFFLINE,
  CHANNEL,
  REGION,
  CITY,
  BRAND,
  PRICEBAND,
  SEGFEAT,
  ADDFEAT,
  FIRST_ACTIVITY_START_DATE,
  FIRST_ACTIVITY_END_DATE,
  AVERAGE_PRICE_MIN,
  AVERAGE_PRICE_MAX,
  MODEL,
  CURRENCY,
  SELECT,
  CATEGORY,
  COUNTRIES_FEATURE,
}

export const FILTER_KEY_ORDER: Record<FilterKeys, number> = {
  [FilterKeys.PRODUCT_GROUP]: 0,
  [FilterKeys.COUNTRY]: 1,
  [FilterKeys.CURRENCY]: 2,
  [FilterKeys.ONLINE_OFFLINE]: 3,
  [FilterKeys.CHANNEL]: 4,
  [FilterKeys.REGION]: 5,
  [FilterKeys.CITY]: 6,
  [FilterKeys.BRAND]: 7,
  [FilterKeys.PRICEBAND]: 8,
  [FilterKeys.SEGFEAT]: 9,
  [FilterKeys.ADDFEAT]: 10,
  [FilterKeys.FIRST_ACTIVITY_START_DATE]: 11,
  [FilterKeys.FIRST_ACTIVITY_END_DATE]: 12,
  [FilterKeys.AVERAGE_PRICE_MIN]: 13,
  [FilterKeys.AVERAGE_PRICE_MAX]: 14,
  [FilterKeys.MODEL]: 15,
  [FilterKeys.SELECT]: 16,
  [FilterKeys.CATEGORY]: 17,
};

export const FILTER_CHIP_ORDER: Record<FilterKeys, number> = {
  [FilterKeys.COUNTRY]: 0,
  [FilterKeys.PRODUCT_GROUP]: 1,
  [FilterKeys.CURRENCY]: 2,
  [FilterKeys.ONLINE_OFFLINE]: 3,
  [FilterKeys.CHANNEL]: 4,
  [FilterKeys.REGION]: 5,
  [FilterKeys.CITY]: 6,
  [FilterKeys.BRAND]: 7,
  [FilterKeys.PRICEBAND]: 8,
  [FilterKeys.SEGFEAT]: 9,
  [FilterKeys.ADDFEAT]: 10,
  [FilterKeys.FIRST_ACTIVITY_START_DATE]: 11,
  [FilterKeys.FIRST_ACTIVITY_END_DATE]: 12,
  [FilterKeys.AVERAGE_PRICE_MIN]: 13,
  [FilterKeys.AVERAGE_PRICE_MAX]: 14,
  [FilterKeys.MODEL]: 15,
  [FilterKeys.SELECT]: 16,
  [FilterKeys.CATEGORY]: 17,
};

export enum FilterGroups {
  BASE,
  MARKET_DEFINITION,
  PRODUCT_SPECIFICATION,
  FIRST_ACTIVITY,
  AVERAGE_PRICE,
}

/**
 * Enum for Filter title in pages config
 */
export enum FilterTitle {
  PRODUCT_GROUP = 'Product Group',
  COUNTRY = 'Country',
  ONLINE_OFFLINE = 'Online Offline',
  REGION = 'Region',
  CITY = 'City',
  BRAND = 'Brand',
  CHANNEL = 'Channel',
  CURRENCY = 'Currency',
  SELECT = 'Select',
  CATEGORY = 'Category',
  COUNTRIES_FEATURE = 'Country Feature',
}

export enum DataType {
  CATEGORY = 'CATEGORY',
  COUNTRIES = 'COUNTRIES',
}

export const FilterTypes = {
  PRODUCT_GROUP: {
    key: FilterKeys.PRODUCT_GROUP,
    title: FilterTitle.PRODUCT_GROUP,
  },
  COUNTRY: {
    key: FilterKeys.COUNTRY,
    title: FilterTitle.COUNTRY,
  },
  ONLINE_OFFLINE: {
    key: FilterKeys.ONLINE_OFFLINE,
    title: FilterTitle.ONLINE_OFFLINE,
  },
  REGION: {
    key: FilterKeys.REGION,
    title: FilterTitle.REGION,
  },
  CITY: {
    key: FilterKeys.CITY,
    title: FilterTitle.CITY,
  },
  BRAND: {
    key: FilterKeys.BRAND,
    title: FilterTitle.BRAND,
  },
  CHANNEL: {
    key: FilterKeys.CHANNEL,
    title: FilterTitle.CHANNEL,
  },
};

export interface Filters {
  dataType: string;
  feature: string;
  filterTitle: string;
  filterKey: FilterKeys;
  options: Option[];
  disabled: boolean;
  isExclusion?: boolean;
}

export type FilterKeyTitleAndExclusion = Pick<Filters, 'filterTitle' | 'filterKey' | 'isExclusion'>;

export interface SubGroupFilter {
  subGroupTitle: string;
  filters: Filters[];
}

export interface CountryFilterPair {
  country: string;
  periodType: string;
  filter: Filters;
  scopeMetadata: ScopeMetadata;
  timePeriod?: TimePeriod;
}

export interface CountryMasterPgsFilterPair {
  country: string;
  timePeriod: TimePeriodPeriodicity;
}

export interface TimePeriod {
  periodName: string;
  filter: Option[];
}

export interface TimePeriodPeriodicity {
  weekly?: OptionPeriodicity[];
  monthly?: OptionPeriodicity[];
}

export interface ScopeMetadata {
  periodType: Periodicity;
  metadataValue: string;
}

export interface MultiCountryObject {
  dataType: string;
  feature: string;
  filterKey: number;
  filterTitle: string;
  options: MultiCountryOptions[];
}

export interface MultiCountryOptions {
  value: string;
  label: string;
}

export interface FilterObject {
  mainTitle: string;
  filterGroup: FilterGroups;
  hasSubGroup: boolean;
  hasNormalAndSubGroup: boolean;
  subGroupFilters: SubGroupFilter[];
  filters: Filters[];
}

export interface SelectedFilters {
  pageType: PageType;
  selectedFilters: Filters[];
}

export interface ProductGroups {
  pageType: PageType;
  option: Option[];
}

export interface Country {
  id: string;
  name: string;
  currency: string;
  currencyLabel?: string;
}

export interface FactRevenue {
  currencySymbol: string;
  currencyLabel?: string;
}

export interface Option {
  value: string;
  label: string;
  isDisabled?: boolean;
}

export interface OptionPeriodicity {
  value: string;
  label: string;
  availablePgs: Option[];
}

export interface FeatureOption {
  value: string;
  label: string;
  dataType: string;
  feature: string;
  filterKey: FilterKeys;
}

export enum ShareType {
  MarketShare = 'SHARE',
  MyMix = 'MIX',
  Absolute = 'ABSOLUTE',
}

export interface UserFilterPermissions {
  access: FilterAccess;
  user: {
    id: string;
    givenName: string;
    familyName: string;
    email: string;
  };
}

export interface SavedFilters {
  id: string;
  name: string;
  country?: string;
  periodicity?: Periodicity;
  clientId?: number;
  filters: Filters[];
  namespace: string;
  permissions: UserFilterPermissions[];
  viewed?: boolean;
  createdAt: number;
}

export interface AllSavedFilters {
  pageType: PageType;
  savedFilters: SavedFilters[];
}

export interface SelectedPgCheckParam {
  setShowPgModal: Dispatch<SetStateAction<boolean>>;
  sideFiltersState: SideFiltersState;
  pagesConfig: PagesConfig;
  selectedProductGroup: Option[] | undefined;
  productType: ProductType;
  productDataType: ProductDataType;
  pageType: PageType;
  pageName?: PageName;
  appliedCountries: Option[];
  timeFrame: Periodicity[];
}

export interface ResponseConfig {
  pageType: PageType;
  response: FilterObject[];
}

export interface ProductGroupFeatureValue {
  productGroup: string;
  sideMenuFilterDtoList: Filters[];
}

export interface ProductGroupPriceBandFeatureValue {
  productGroup: string;
  oldProductGroup: string;
  sideMenuFilterDtoList: Filters[];
}

export interface ProductGroupPriceBandValue {
  country: string;
  periodType: string;
  productGroupSideMenuFilterDtoList: ProductGroupPriceBandFeatureValue[];
}

export const getPageTypeWithPageName = (pageName: PageName) => {
  switch (pageName) {
    case PageName.RETAILER_OVERVIEW:
    case PageName.CATEGORY_LEADERBOARD:
      return PageType.RETAILER_OVERVIEW;
    case PageName.OPPORTUNITY_REVIEW:
    case PageName.PERFORMANCE_ANALYSIS:
    case PageName.TRADING_ASSISTANT:
      return PageType.OPPORTUNITY_REVIEW;
    case PageName.RI_PRODUCT:
    case PageName.RI_PRODUCT_COMPARE:
    case PageName.RI_PRODUCT_TRENDS:
      return PageType.RI_PRODUCT;
    case PageName.EXPLORE:
      return PageType.EXPLORE;
  }
};

export interface ExportContext extends LoadedProductGroups, LoadedCountries {
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  timePeriod: string;
  comparisonTimePeriod: string;
  periodType: string;
  showPerformanceWithin: string;
  appliedFiltersText: string;
  pageName?: PageName;
  sortedBy?: string;
  pagesConfig?: any;
  pageView?: ExportTrackingPageView;
  segmentBy?: string;
  selectedKpi?: string;
}

export interface ExportFileName {
  title: string;
  appliedCountry: Option[] | undefined;
  appliedPg: Option[] | undefined;
  timeframe: string;
  comparisonTimePeriod: string;
  exportFormat: ExportFormat;
  selectedKpi: Option;
  viewType?: PerformanceAnalysisView;
  pageName?: PageName;
}

export interface UserInfo {
  userId: string;
  email: string;
  givenName: string;
  familyName: string;
}

export enum AccessLevel {
  Owner = 'owner',
  Viewer = 'viewer',
}

export interface UserAccess {
  accessLevel: AccessLevel;
  //Individual User For Preference
  userInfo?: UserInfo;
  //User Groups For Preference
  userGroupInfo?: {
    userGroupId: number;
    userGroupName: string;
    accessLevel: AccessLevel;
    clientId: number;
    usersCount?: number;
  };
}

export interface CurrencyExchangeRatePerCountry {
  country: string;
  currencyExchangeRate: Record<string, number>;
}
